import { CourseMappingCellType } from '../../interfaces/CourseMappingInterface';
import { CourseSearchFilterParams } from '../../interfaces/CoursesInterface';
import { SubjectArea } from '../../interfaces/EOAInterface';
import {
  CourseMappingDropdownOption,
  LANGUAGE_LEVEL_OPTIONS,
  toSubjectAreaOptions,
} from './courseMappingDropdownOptions';

export type CourseMappingFilterControl = 'multi' | 'text' | 'number' | 'boolean' | 'select';

export type CourseMappingFilterValue = string | string[];

export interface CourseMappingFilterField {
  key: string;
  label: string;
  param: keyof CourseSearchFilterParams;
  control: CourseMappingFilterControl;
  cellType: CourseMappingCellType;
}

export interface CourseMappingActiveFilter {
  key: string;
  value: CourseMappingFilterValue;
}

export type CourseMappingFilterState = CourseMappingActiveFilter[];

export interface CourseMappingFilterOptionsData {
  subjectAreas: SubjectArea[];
  schools: string[];
  academicPeriods: string[];
}

export const COURSE_MAPPING_FILTER_FIELDS: CourseMappingFilterField[] = [
  { key: 'name', label: 'Course name', param: 'course_name', control: 'text', cellType: 'locked' },
  { key: 'code', label: 'Course ID', param: 'course_code', control: 'text', cellType: 'locked' },
  { key: 'schoolName', label: 'School name', param: 'school_name', control: 'multi', cellType: 'locked' },
  { key: 'academicPeriod', label: 'Year', param: 'academic_period', control: 'multi', cellType: 'locked' },
  { key: 'subjectArea', label: 'Subject area', param: 'subject_area', control: 'multi', cellType: 'dropdown' },
  {
    key: 'subjectSubcategory',
    label: 'Subject subcategory',
    param: 'subject_area_subcategory',
    control: 'multi',
    cellType: 'dropdown',
  },
  { key: 'numberOfCredits', label: 'Credits', param: 'number_of_credits', control: 'number', cellType: 'editable' },
  {
    key: 'languageCategory',
    label: 'Language category',
    param: 'language_category',
    control: 'text',
    cellType: 'dropdown',
  },
  { key: 'languageLevel', label: 'Language level', param: 'language_level', control: 'select', cellType: 'dropdown' },
  {
    key: 'isCollegePreparatory',
    label: 'College prep',
    param: 'is_college_preparatory',
    control: 'boolean',
    cellType: 'checkbox',
  },
  { key: 'isHonors', label: 'Honors', param: 'is_honors', control: 'boolean', cellType: 'checkbox' },
  { key: 'isAp', label: 'AP', param: 'is_ap', control: 'boolean', cellType: 'checkbox' },
  { key: 'isIb', label: 'IB', param: 'is_ib', control: 'boolean', cellType: 'checkbox' },
  { key: 'isAice', label: 'AICE', param: 'is_aice', control: 'boolean', cellType: 'checkbox' },
  {
    key: 'isDualEnrollment',
    label: 'Dual enrollment',
    param: 'is_dual_enrollment',
    control: 'boolean',
    cellType: 'checkbox',
  },
  { key: 'isCte', label: 'CTE', param: 'is_cte', control: 'boolean', cellType: 'checkbox' },
  { key: 'isLabScience', label: 'Lab science', param: 'is_lab_science', control: 'boolean', cellType: 'checkbox' },
];

export const findFilterField = (key: string): CourseMappingFilterField | undefined =>
  COURSE_MAPPING_FILTER_FIELDS.find((field) => field.key === key);

export const initialFilterValue = (field: CourseMappingFilterField): CourseMappingFilterValue =>
  field.control === 'multi' ? [] : '';

export const BOOLEAN_FILTER_OPTIONS: CourseMappingDropdownOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const collectSubcategoryOptions = (subjectAreas: SubjectArea[]): CourseMappingDropdownOption[] => {
  const names = new Set<string>();

  subjectAreas.forEach((area) => {
    area.subcategories.forEach((subcategory) => {
      if (subcategory.name.trim()) {
        names.add(subcategory.name.trim());
      }
    });
  });

  return Array.from(names)
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .map((name) => ({ value: name, label: name }));
};

export const getFilterFieldOptions = (
  field: CourseMappingFilterField,
  data: CourseMappingFilterOptionsData
): CourseMappingDropdownOption[] => {
  switch (field.key) {
    case 'schoolName':
      return data.schools.map((value) => ({ value, label: value }));
    case 'academicPeriod':
      return data.academicPeriods.map((value) => ({ value, label: value }));
    case 'subjectArea':
      return toSubjectAreaOptions(data.subjectAreas);
    case 'subjectSubcategory':
      return collectSubcategoryOptions(data.subjectAreas);
    case 'languageLevel':
      return LANGUAGE_LEVEL_OPTIONS;
    default:
      return field.control === 'boolean' ? BOOLEAN_FILTER_OPTIONS : [];
  }
};

export const isFilterApplied = (field: CourseMappingFilterField, value: CourseMappingFilterValue): boolean => {
  switch (field.control) {
    case 'multi':
      return Array.isArray(value) && value.length > 0;
    case 'text':
      return typeof value === 'string' && value.trim() !== '';
    case 'number':
      return typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value));
    case 'select':
      return typeof value === 'string' && value !== '';
    case 'boolean':
      return value === 'true' || value === 'false';
    default:
      return false;
  }
};

export const buildCourseFilterParams = (state: CourseMappingFilterState): CourseSearchFilterParams => {
  const params: Record<string, string[] | number | boolean> = {};

  COURSE_MAPPING_FILTER_FIELDS.forEach((field) => {
    const active = state.find((entry) => entry.key === field.key);
    if (!active || !isFilterApplied(field, active.value)) {
      return;
    }

    switch (field.control) {
      case 'multi':
        params[field.param] = active.value as string[];
        break;
      case 'text':
        params[field.param] = [(active.value as string).trim()];
        break;
      case 'number':
      case 'select':
        params[field.param] = Number(active.value);
        break;
      case 'boolean':
        params[field.param] = active.value === 'true';
        break;
    }
  });

  return params as CourseSearchFilterParams;
};

export const serializeFilterParams = (state: CourseMappingFilterState): string =>
  JSON.stringify(buildCourseFilterParams(state));

export const getFilterValueSummary = (
  field: CourseMappingFilterField,
  value: CourseMappingFilterValue,
  options: CourseMappingDropdownOption[]
): string => {
  const labelFor = (optionValue: string) =>
    options.find((option) => option.value === optionValue)?.label ?? optionValue;

  switch (field.control) {
    case 'multi':
      return (value as string[]).map(labelFor).join(', ');
    case 'boolean':
    case 'select':
      return labelFor(value as string);
    default:
      return value as string;
  }
};
